/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { AfterViewInit, Component, HostListener, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CommonHistory } from '../../../core/model/common-history.model';
import { HistoryOps } from '../../../core/model/history-ops.model';
import { MenuItem } from '../../../core/model/menu-item.model';
import { TabSearchParameter } from '../../../core/model/tab-search-parameter.model';
import { AppUtilityService } from '../../../core/services/app-utility.service';
import { LmsLoaderService } from '../../../core/services/lms-loader.service';
import { LmsMenuService } from '../../../core/services/lms-menu.service';
import { LmsRendererService } from '../../../core/services/lms-renderer.service';


@Component({
  selector: 'lms-lms-history-data',
  templateUrl: './lms-history-data.component.html',
  styleUrls: ['./lms-history-data.component.scss']
})
export class LmsHistoryDataComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() history: Array<CommonHistory>;
  @Input() historyOps: HistoryOps;
  @Input() scrollHeight: number;
  @Input() isBackToTop: boolean;
  @Input() upperHeight: number;

  historyToShow: Array<CommonHistory>;
  rowsPerPage: number;
  fromDate: string;
  toDate: string;
  totalRecords: number;
  firstIndex: number;
  lastIndex: number;
  showingFrom: number;
  showingTo: number;
  loadingStatus = false;
  columnAlignStyle: string;
  isHistorySorted = false;
  isHeaderAdjusted = false;

  historyData: Map<string, Array<CommonHistory>>;
  scrollableBody: any;

  locoLinks: Array<MenuItem>;
  trainLinks: Array<MenuItem>;
  overlayLinks: Array<MenuItem>;
  overlayPanel: OverlayPanel;
  panelVisible = false;
  contextType: string;
  contextSearchText: string;

  @ViewChild('viewPortPaging') viewPageDirective;
  /**
   * Creates an instance of LmsHistoryDataComponent.
   * @param {Router} router
   * @param {LmsLoaderService} lmsLoaderService
   * @memberOf LmsHistoryDataComponent
   */
  constructor(private router: Router,
    private lmsLoaderService: LmsLoaderService,
    private appUtilityService: AppUtilityService,
    private lmsMenuService: LmsMenuService,
    private lmsRendererService: LmsRendererService) {
    this.locoLinks = this.lmsMenuService.getMenuItem('Locomotive').subTypes;
    this.trainLinks = this.lmsMenuService.getMenuItem('Train').subTypes;
    this.historyData = new Map();
    this.historyToShow = [];
    this.lmsRendererService.historyOverlayObs.subscribe((overlay) => {
      if (overlay && overlay === 'THPHIS') {
        this.checkForOverlayPanelVisibility();
      }
    });
  }

  /**
   * @memberOf LmsHistoryDataComponent
   */
  ngOnInit() {
    this.rowsPerPage = this.appUtilityService.getRowPerPageCount(38, this.upperHeight);
  }

  /**
   * this is a life cycle method if this component
   * this method is used to define total records of the history
   * along with the it slice the first set of data to be displayed
   * @memberOf LmsHistoryDataComponent
   */
  ngOnChanges() {
    if (this.history && this.history.length > 0) {
      this.rowsPerPage = this.appUtilityService.getRowPerPageCount(38, this.upperHeight);
      this.historyToShow = this.history.slice(0, (this.history.length > this.rowsPerPage ? this.rowsPerPage : this.history.length));
      this.firstIndex = this.history.indexOf(this.historyToShow[0]);
      this.lastIndex = this.history.indexOf(this.historyToShow[this.historyToShow.length - 1]);
      this.totalRecords = this.history.length;
      setTimeout(() => {
        this.checkWhetherHeaderAdjusted();
        this.viewPageDirective.updateScreenSizeSetting();
      }, 20);
    }
    this.prepareFromToDates();
  }

  getPaginationData($event) {
    this.showingFrom = $event.paging.start;
    this.showingTo = $event.paging.end;
    this.checkForOverlayPanelVisibility();
  }

  /**
   * this is a life cycle method of this component
   * this is used to bind a scroll event to history table's
   * body part
   * @memberOf LmsHistoryDataComponent
   */
  ngAfterViewInit() {
    this.checkWhetherHeaderAdjusted();
  }

  private checkWhetherHeaderAdjusted() {
    const scrollableBody = document.getElementById('commonHistoryData');
    const tableHeader = document.getElementById('commonHistoryHeader');
    if (tableHeader && scrollableBody) {
      const items = scrollableBody.querySelectorAll('.commHistoryTableBody');
      if ((scrollableBody.clientHeight / 40) < items.length) {
        const scrollWidth = scrollableBody.offsetWidth - scrollableBody.clientWidth;
        tableHeader.setAttribute('style', `margin-right: ${scrollWidth}px !important`);
        this.isHeaderAdjusted = true;
      } else {
        tableHeader.removeAttribute('style');
        this.isHeaderAdjusted = false;
      }
    }
  }

  /**
   * this method is used to next set of data during lazy
   * loading of data of history table
   * @private
   * @memberOf LmsHistoryDataComponent
   */
  private loadNextSetofData() {
    this.historyToShow = this.history.slice(0, (this.history.length > (this.lastIndex + this.rowsPerPage) ? (this.lastIndex + this.rowsPerPage) : this.history.length));
    this.firstIndex = this.history.indexOf(this.historyToShow[0]);
    this.lastIndex = this.history.indexOf(this.historyToShow[this.historyToShow.length - 1]);
  }

  /**
   * this mehtod is used to handle the scroll event on history table to support
   * lazy loading and pagination of that table
   * @memberOf LmsHistoryDataComponent
   */
  public scrollEventOccured() {
    const scrollableBody = document.getElementById('commonHistoryData');
    if (scrollableBody) {
      const clientHeight = scrollableBody.clientHeight;
      const scrollHeight = scrollableBody.scrollHeight;
      const scrollTop = scrollableBody.scrollTop;
      const diff = (clientHeight + scrollTop) - scrollHeight;
      if (diff <= 2 && diff >= -2) {
        this.loadNextSetofData();
      }
      if (this.viewPageDirective) {
        this.viewPageDirective.updateScreenSizeSetting();
      }
    }
  }

  /**
   * this method is used to get back to top
   * position of history table
   * @memberOf LmsHistoryDataComponent
   */
  public scrollTableScrollToTop() {
    const scrollableBody = document.getElementById('commonHistoryData');
    if (scrollableBody) {
      scrollableBody.scrollTop = 0;
      this.historyToShow = this.history.slice(0, 30);
      this.firstIndex = this.history.indexOf(this.historyToShow[0]);
      this.lastIndex = this.history.indexOf(this.historyToShow[this.historyToShow.length - 1]);
    }
  }

  /**
   * this method is used to prepare the from to dates
   * for the history shown for a loco/train
   * @memberOf LmsHistoryDataComponent
   */
  public prepareFromToDates() {
    const date = new Date();
    this.toDate = `${('0' + (date.getMonth() + 1)).slice(-2)}/${('0' + (date.getDate())).slice(-2)}/${date.getFullYear()}`;
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - this.historyOps.days);
    this.fromDate = `${('0' + (fromDate.getMonth() + 1)).slice(-2)}/${('0' + (fromDate.getDate())).slice(-2)}/${fromDate.getFullYear()}`;
  }

  setOverlayLinks(type: string) {
    this.checkForOverlayPanelVisibility();
    if (type === 'loco') {
      this.overlayLinks = this.locoLinks;
    } else if (type === 'train') {
      this.overlayLinks = this.trainLinks;
    }
  }

  public locomotiveOverlayPosition(event, panel: OverlayPanel, id: string, index: string, obj: string) {
    this.overlayPanel = panel;
    if (!panel.overlayVisible && this.panelVisible === true) {
      this.panelVisible = false;
    }
    if (this.panelVisible) {
      panel.hide();
      this.panelVisible = false;
      event.stopPropagation();
      return;
    }
    const overlayPanel = document.getElementById('overlayLinksInfo').getElementsByClassName('p-overlaypanel');
    let element;
    if (obj === 'loco') {
      element = document.getElementById('locomotive' + index + 'CommHis' + id);
      this.contextType = 'Locomotive';
      this.contextSearchText = id;
    } else if (obj === 'train') {
      element = document.getElementById('train' + index + 'CommHis' + id);
      this.contextType = 'Train';
      this.contextSearchText = id;
    }
    panel.show(event);
    const rect = element.getBoundingClientRect();
    const scrollHeight = window.pageYOffset;
    const windowHeight = window.innerHeight;
    let overlayHeight = 0;
    if (this.overlayLinks && this.overlayLinks.length > 0) {
      overlayHeight = this.overlayLinks.length * 30;
    } else {
      panel.hide();
      return;
    }
    let top;
    if ((windowHeight - rect.top - rect.height) > overlayHeight) {
      top = rect.top + rect.height + scrollHeight + 'px';
    } else {
      top = (rect.top - overlayHeight) + scrollHeight + 'px';
    }
    const left = rect.left + 'px';
    const width = rect.width + 'px';
    let style = `display: block; z-index: 1001; visibility: visible; 
            opacity: 1.032; top: <<top>>; left: <<left>>; width: <<width>>;`;
    style = style.replace('<<top>>', top);
    style = style.replace('<<left>>', left);
    style = style.replace('<<width>>', width);
    overlayPanel[0].setAttribute('style', style);
    event.stopPropagation();
    this.panelVisible = true;
    this.lmsRendererService.updateOverlayVisibility('COMMHIS');
  }

  public hideLocoOverlayPanel(event, panel: OverlayPanel) {
    this.checkForOverlayPanelVisibility();
    if (this.panelVisible) {
      panel.hide();
      this.panelVisible = false;
      panel = null;
      return;
    }
  }

  checkForOverlayPanelVisibility() {
    if (this.overlayPanel) {
      this.overlayPanel.hide();
      this.overlayPanel = null;
      this.contextType = null;
      this.contextSearchText = null;
      this.overlayLinks = null;
    }
  }

  @HostListener('document:click') hideAddTabPanel(event) {
    this.checkForOverlayPanelVisibility();
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * this method is used to route/navigate user to given screen.
   * user clicks on link on history table and move to that perticular
   * screen.
   * @param {string} subType
   * @param {string} type
   * @param {string} searchTex
   * @memberOf LmsHistoryDataComponent
   */
  public routeToPage(subType: string, type: string, searchText: string) {
    const tabSearchParameter = <TabSearchParameter>({ searchText: searchText });
    window.open(
      window.location.href.split('?')[0] + '?type='+ type.toLowerCase() +'&subtype=' + subType.toLowerCase() + '&search=' + tabSearchParameter.searchText,
      '_blank', "scrollbars=yes,resizable=yes,top=100,left=500,width=1000,height=800"
    );
  }

  public getFormattedLocoId(locomotiveId: string) {
    if (locomotiveId) {
      return this.appUtilityService.UIformatLocomotiveId(locomotiveId);
    }
  }

}
